import React, { useState, useEffect } from "react";
import { FaEnvelope, FaLock, FaEye, FaEyeSlash, FaSpinner, FaCheckCircle } from "react-icons/fa";
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from "jwt-decode";
import { useNavigate, Link } from "react-router-dom";
import Logo from "../Assets/HD Quality Logo.svg";
import LogoDark from "../Assets/HD Quality Logo 2.svg";
import { useTheme } from "../context/SocketContext";
import ThemeCustomizer from "../components/ThemeCustomizer";
import { toast } from "../components/ui/Toast";

const notifyAuthChanged = () => {
  window.dispatchEvent(new Event("bookscomply-auth-changed"));
};

const FEATURES = [
  "Professional Tax Filing Services",
  "Expert Bookkeeping Solutions",
  "USA Company Formation",
];

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const impersonateData = urlParams.get('impersonate');

      if (impersonateData) {
        try {
          const sessionData = JSON.parse(decodeURIComponent(impersonateData));
          localStorage.setItem("token", sessionData.token);
          localStorage.setItem("userEmail", sessionData.userEmail);
          localStorage.setItem("userName", sessionData.userName);
          localStorage.setItem("isImpersonating", "true");
          localStorage.setItem("adminSession", JSON.stringify(sessionData.adminSession));
          notifyAuthChanged();
          window.history.replaceState({}, document.title, "/dashboard");
          navigate("/dashboard");
          return;
        } catch (error) {
          console.error("Error processing impersonation data:", error);
        }
      }

      const token = localStorage.getItem("token");
      if (token) {
        try {
          const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/user`, {
            headers: { Authorization: `Bearer ${token}` },
          });

          if (response.ok) {
            const data = await response.json();
            if (data.user?.isBookkeepingCollaborator || data.user?.isAssignee) {
              navigate("/bookkeeping");
            } else {
              navigate("/dashboard");
            }
          }
        } catch (error) {
          console.error("Token validation failed:", error);
        }
      }
    };

    checkAuth();
  }, [navigate]);

  const redirectAfterLogin = (user) => {
    if (user?.isBookkeepingCollaborator || user?.isAssignee) {
      navigate("/bookkeeping");
      return;
    }
    if (user.isUSAEnabled && user.isTaxFilingEnabled &&
        user.isBookkeepingEnabled && user.isCFOEnabled) {
      navigate("/tax-filing");
    } else if (user.isUSAEnabled) {
      navigate("/usa-company-formation");
    } else if (user.isTaxFilingEnabled) {
      navigate("/tax-filing");
    } else if (user.isBookkeepingEnabled) {
      navigate("/bookkeeping");
    } else if (user.isCFOEnabled) {
      navigate("/cfo-advisory");
    } else {
      navigate("/pricing");
    }
  };

  const handleOAuthLogin = async (_provider, token) => {
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/google-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Login failed.");
      }

      const data = await response.json();
      localStorage.setItem("token", data.token);
      notifyAuthChanged();
      toast.success("Login successful! Redirecting...");

      setTimeout(() => {
        redirectAfterLogin(data.user);
      }, 1000);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Login failed.");
      }

      const data = await response.json();
      localStorage.setItem("token", data.token);
      localStorage.setItem("userId", data.user._id);
      notifyAuthChanged();
      toast.success("Login successful! Redirecting...");

      setTimeout(() => {
        redirectAfterLogin(data.user);
      }, 1000);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <GoogleOAuthProvider clientId="383385845506-jknb7cqrrmbmhnki2vbdkindqdpmpce8.apps.googleusercontent.com">
      <div className="min-h-screen flex flex-col lg:flex-row">
        {/* Left — Branding */}
        <div
          className="relative hidden lg:flex lg:w-1/2 lg:min-h-screen overflow-hidden"
          style={{ background: 'linear-gradient(135deg, var(--primary-600), var(--primary-700), var(--primary-900))' }}
        >
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-40 -right-40 w-80 h-80 bg-secondary-500/30 rounded-full blur-3xl animate-pulse-slow" />
            <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-primary-400/30 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }} />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
            <div className="absolute top-20 left-20 w-20 h-20 bg-gradient-to-br from-secondary-400 to-secondary-600 rounded-2xl opacity-20 animate-float rotate-12" />
            <div className="absolute top-1/3 right-1/4 w-12 h-12 bg-gradient-to-br from-white to-gray-200 rounded-lg opacity-10 animate-float" style={{ animationDelay: '1s' }} />

            {/* Bottom-right wave & arc decoration */}
            <svg
              className="absolute -bottom-4 -right-4 h-[280px] w-[320px] text-white/20"
              viewBox="0 0 320 280"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M320 280C320 180 220 120 120 120C40 120 0 200 0 280"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path
                d="M320 280C320 200 240 150 140 150C60 150 20 220 0 280"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                opacity="0.7"
              />
              <path
                d="M320 280C320 220 260 185 170 185C90 185 40 245 0 280"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                opacity="0.5"
              />
              <circle cx="260" cy="220" r="88" stroke="currentColor" strokeWidth="1.5" opacity="0.35" />
              <circle cx="260" cy="220" r="64" stroke="currentColor" strokeWidth="1.5" opacity="0.25" />
              <circle cx="260" cy="220" r="40" stroke="currentColor" strokeWidth="1.5" opacity="0.18" />
            </svg>
          </div>

          <div className="absolute top-8 left-8 xl:top-10 xl:left-12 z-20">
            <img
              src={LogoDark}
              alt="BooksComply"
              className="h-9 xl:h-10 w-auto drop-shadow-sm"
            />
          </div>

          <div className="relative z-10 flex flex-col justify-center w-full px-12 xl:px-16 py-12 text-white">
            <h2 className="text-3xl xl:text-4xl font-bold mb-4 leading-tight">Welcome to BooksComply</h2>
            <p className="text-base xl:text-lg text-white/80 max-w-md leading-relaxed">
              Your trusted partner for accounting, tax filing, and business compliance services.
            </p>

            <ul className="mt-10 space-y-4">
              {FEATURES.map((feature) => (
                <li key={feature} className="flex items-center gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/20">
                    <FaCheckCircle className="h-5 w-5" />
                  </span>
                  <span className="text-sm xl:text-base text-white/90">{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Right — Login form */}
        <div className="relative flex flex-1 flex-col bg-white dark:bg-dark-900 min-h-screen lg:min-h-0 lg:w-1/2">
          <div className="absolute top-5 right-5 z-40">
            <ThemeCustomizer variant="auth" />
          </div>

          <div className="flex flex-1 items-center justify-center px-5 py-10 sm:px-8 md:px-12 lg:px-16">
            <div className="w-full max-w-[420px] animate-scale-in">
              <div className="lg:hidden text-center mb-8">
                <img
                  src={isDarkMode ? LogoDark : Logo}
                  alt="BooksComply"
                  className="h-10 w-auto mx-auto"
                />
              </div>

              <div className="mb-8 text-center lg:text-left">
                <h1 className="page-title mb-2">Welcome Back</h1>
                <p className="page-subtitle">Login to your account to continue</p>
              </div>

              <form onSubmit={handleEmailLogin} className="space-y-4">
                <div className="relative group">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
                    <FaEnvelope className="h-4 w-4 text-gray-400 group-focus-within:text-primary-500 transition-colors" />
                  </div>
                  <input
                    type="email"
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 py-3.5 pl-11 pr-4 text-sm text-dark-800 placeholder-gray-400 transition-all duration-200 focus:border-primary-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-dark-700 dark:bg-dark-800 dark:text-white dark:focus:bg-dark-800"
                    placeholder="Email Address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>

                <div className="relative group">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
                    <FaLock className="h-4 w-4 text-gray-400 group-focus-within:text-primary-500 transition-colors" />
                  </div>
                  <input
                    type={showPassword ? "text" : "password"}
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 py-3.5 pl-11 pr-11 text-sm text-dark-800 placeholder-gray-400 transition-all duration-200 focus:border-primary-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-dark-700 dark:bg-dark-800 dark:text-white dark:focus:bg-dark-800"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400 transition-colors hover:text-primary-500"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <FaEyeSlash className="h-4 w-4" /> : <FaEye className="h-4 w-4" />}
                  </button>
                </div>

                <div className="flex justify-end pt-1">
                  <Link
                    to="/forgetpassword"
                    className="text-sm font-medium text-primary-600 transition-colors hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                  >
                    Forgot Password?
                  </Link>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="flex w-full items-center justify-center gap-2 rounded-xl py-3.5 text-sm font-semibold text-white shadow-lg transition-all duration-200 hover:brightness-105 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-70"
                  style={{
                    background: 'linear-gradient(135deg, var(--primary-600), var(--primary-500))',
                    boxShadow: '0 10px 15px -3px var(--glow-color)',
                  }}
                >
                  {loading ? (
                    <>
                      <FaSpinner className="h-4 w-4 animate-spin" />
                      Logging in...
                    </>
                  ) : (
                    "Login"
                  )}
                </button>
              </form>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200 dark:border-dark-700" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-white px-4 text-gray-500 dark:bg-dark-900 dark:text-gray-400">or continue with</span>
                </div>
              </div>

              <div className="flex justify-center">
                <GoogleLogin
                  onSuccess={(credentialResponse) => {
                    try {
                      jwtDecode(credentialResponse.credential);
                      handleOAuthLogin("Google", credentialResponse.credential);
                    } catch (error) {
                      console.error("Error decoding token:", error);
                      toast.error("Failed to decode token. Please try again.");
                    }
                  }}
                  onError={() => {
                    toast.error("Google login failed. Please try again.");
                  }}
                  theme={isDarkMode ? "filled_black" : "outline"}
                  size="large"
                  shape="rectangular"
                  width="280"
                />
              </div>

              <p className="mt-8 text-center text-sm text-gray-600 dark:text-gray-400">
                Don&apos;t have an account?{" "}
                <Link
                  to="/signup"
                  className="font-semibold text-primary-600 transition-colors hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                >
                  Sign Up
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </GoogleOAuthProvider>
  );
};

export default LoginPage;
